/**
 * Stimulus materials ("attachments") of template-based focus groups.
 *
 * Each file is uploaded once, as soon as it is added (POST /api/v1/files, one
 * call per file), for the template's domain; the run then references the files
 * by file_id, in order, as `attachment_ids` in the JSON start request. Kept
 * free of Vue and `@/` imports so templateAttachment.test.mjs runs under plain
 * Node.
 */
import { templateShowsAttachment } from './meetingTemplateSteps.js';

// The backend's defaults (MAX_ATTACHMENT_PAGES, MAX_UPLOAD_MB), which no
// endpoint exposes. The form checks them early; the backend stays the
// authority and its errors are shown regardless.
export const MAX_ATTACHMENT_PAGES = 10; // per file at upload, and in total per run
export const MAX_UPLOAD_MB = 25; // per file

/**
 * A run's file as a focus-group result stores it: a reference without links.
 *
 * @typedef {Object} AttachmentRef
 * @property {string} file_id
 * @property {string} filename
 * @property {'image'|'pdf'|'pptx'} kind
 * @property {string} mime_type
 * @property {number} page_count an image counts as one page
 */

/**
 * POST /api/v1/files and GET /api/v1/files/{file_id}: an AttachmentRef plus
 * `domain`, `created_at` and `pages`, whose links are signed at request time
 * and expire.
 *
 * @typedef {AttachmentRef & {domain: string, pages: {page: number, url: string}[], created_at: string}} StoredFile
 */

/**
 * JSON body of POST /api/start_template_focus_group.
 *
 * @typedef {Object} StartTemplateFocusGroupRequest
 * @property {string} query
 * @property {string} template_id
 * @property {string[]} persona_ids
 * @property {string} campaign_name
 * @property {string} campaign_id
 * @property {string|null} summarizer_prompt_id
 * @property {string|null} improvement_plan_prompt_id
 * @property {string[]} [attachment_ids] in the order the group sees them; omitted
 *   without files, never null (422)
 */

/**
 * @typedef {Object} StartTemplateFocusGroupResponse
 * @property {string} status
 * @property {string} message
 * @property {string} focus_group_id
 * @property {string} campaign_id
 * @property {string} domain
 * @property {string} template_id
 * @property {number} steps_count
 * @property {number} personas_count
 * @property {number} attachments_count 0 without files
 * @property {number} attachment_pages total pages of all the files, 0 without
 */

/**
 * The fields of GET /api/focus-groups/{focus_group_id} read here.
 *
 * @typedef {Object} FocusGroupResult
 * @property {AttachmentRef[]} attachments run order; [] without files, also on
 *   results older than attachments
 * @property {Object[]} steps executed template steps, each with show_attachment
 */

// Icon and i18n label of each `kind` the backend returns.
export const ATTACHMENT_KINDS = {
  image: { icon: 'mdi-image-outline', labelKey: 'attachmentpreview.kindImage' },
  pdf: { icon: 'mdi-file-pdf-box', labelKey: 'attachmentpreview.kindPdf' },
  pptx: { icon: 'mdi-file-powerpoint-box', labelKey: 'attachmentpreview.kindPptx' },
};

// ---------------------------------------------------------------------------
// The start form's list: one item per added file, in the order the group will
// see them. Every item has key (client-side), status, filename, source and
// domain (the one it was uploaded for), plus:
//   uploading  file, progress (0-100)
//   ready      file_id, kind, page_count, pages (signed links, preview only)
//   error      file, error (message to show), retryable
// ---------------------------------------------------------------------------

// Identifies a picked file. Every upload gets a new file_id, so picking the same
// file twice would put the same material in front of the group twice.
export function localFileSignature(file) {
  return [file.name, file.size, file.lastModified].join('|');
}

export function attachmentTooLarge(file) {
  return file.size > MAX_UPLOAD_MB * 1024 * 1024;
}

export function uploadingAttachment(file, key, domain) {
  return {
    key,
    status: 'uploading',
    filename: file.name,
    source: localFileSignature(file),
    domain,
    file,
    progress: 0,
  };
}

// The upload response as the list keeps it: the id to send and what the row
// shows. The File is dropped; the page links expire (~2h) and only serve this
// preview, never persisted or reused.
export function readyAttachment(item, uploaded) {
  return {
    key: item.key,
    status: 'ready',
    filename: uploaded.filename,
    source: item.source,
    domain: uploaded.domain,
    file_id: uploaded.file_id,
    kind: uploaded.kind,
    page_count: uploaded.page_count,
    pages: Array.isArray(uploaded.pages) ? uploaded.pages : [],
  };
}

// Keeps the File, so the upload can be retried when retrying can help.
export function failedAttachment(item, message, retryable) {
  return { ...item, status: 'error', progress: 0, error: message, retryable };
}

// Network errors and 5xx (storage failed or disabled) can pass on a retry; a
// 4xx refusal (type, pages, size, domain) would fail the same way again.
export function uploadErrorRetryable(error) {
  const status = error?.response?.status;
  return !status || status >= 500;
}

export function replaceAttachment(items, key, next) {
  return items.map((item) => (item.key === key ? next : item));
}

// Swaps the upload result in. A file_id is listed at most once (the backend
// refuses duplicate ids), so another item with the same id is dropped.
export function applyUploadedAttachment(items, key, uploaded) {
  const item = items.find((candidate) => candidate.key === key);
  if (!item) return items;
  if (items.some((other) => other.key !== key && other.file_id === uploaded.file_id)) {
    return items.filter((other) => other.key !== key);
  }
  return replaceAttachment(items, key, readyAttachment(item, uploaded));
}

// Uploads, or retries, the listed file `key` with `upload` (upload_file's
// signature). `read` returns the current list and `write` replaces it, so the
// list may change while the request is in flight. The attempt is identified by
// its AbortController: once the file is removed, cleared or retried, the
// result is dropped. `messages` are the texts shown when the backend gives no
// `detail` (uploadFailed) or the file is refused before sending (tooLarge).
export async function uploadListedAttachment(key, { read, write, upload, messages, onError }) {
  const item = read().find((candidate) => candidate.key === key);
  if (!item?.file) return;
  if (attachmentTooLarge(item.file)) {
    // The backend would only answer 413 after the whole transfer.
    write(replaceAttachment(read(), key, failedAttachment(item, messages.tooLarge, false)));
    return;
  }

  const controller = new AbortController();
  write(replaceAttachment(read(), key, { ...uploadingAttachment(item.file, key, item.domain), controller }));
  const current = () => read().find((candidate) => candidate.key === key);
  const isCurrentAttempt = () => current()?.controller === controller;

  try {
    const response = await upload(item.file, item.domain, {
      signal: controller.signal,
      onUploadProgress: (event) => {
        const attempt = current();
        if (attempt?.controller === controller && event.total) {
          attempt.progress = Math.round((event.loaded * 100) / event.total);
        }
      },
    });
    if (!isCurrentAttempt()) return;
    write(applyUploadedAttachment(read(), key, response.data));
  } catch (error) {
    if (!isCurrentAttempt()) return;
    onError?.(error);
    // 400 (type, pages, unconvertible file, domain), 403, 413, 500, 503: the
    // backend's message says what to fix.
    const message = apiErrorDetail(error) || messages.uploadFailed;
    write(replaceAttachment(read(), key, failedAttachment(current(), message, uploadErrorRetryable(error))));
  }
}

export function moveAttachment(items, from, to) {
  if (from === to || to < 0 || to >= items.length) return items;
  const next = [...items];
  next.splice(to, 0, ...next.splice(from, 1));
  return next;
}

// Pages of the files ready to send: what the run's total limit applies to.
export function attachmentPageTotal(items) {
  return items.reduce(
    (total, item) => total + (item.status === 'ready' ? item.page_count || 0 : 0),
    0,
  );
}

export function readyAttachmentIds(items) {
  return items.filter((item) => item.status === 'ready').map((item) => item.file_id);
}

// Why the list keeps the start disabled, most actionable first, or null. Every
// file must be ready: one invalid file makes the backend refuse the whole run.
export function attachmentsStartBlocker(items, template) {
  if (!items.length) return null;
  if (items.some((item) => item.status === 'error')) return 'error';
  if (attachmentPageTotal(items) > MAX_ATTACHMENT_PAGES) return 'over_limit';
  if (template && !templateShowsAttachment(template)) return 'not_shown';
  if (items.some((item) => item.status === 'uploading')) return 'uploading';
  return null;
}

// The files were uploaded for one domain: a template of another domain cannot
// take them. No template selected yet decides nothing.
export function attachmentsDomainMismatch(items, template) {
  return !!template && items.some((item) => item.domain !== template.domain_id);
}

// file_ids of listed files that a start error names in its detail
// ("File '<id>' not found", "Duplicate attachment ids: <a>, <b>").
export function attachmentsNamedIn(detail, items) {
  if (!detail) return [];
  return items
    .filter((item) => item.file_id && detail.includes(item.file_id))
    .map((item) => item.file_id);
}

// JSON body of POST /api/start_template_focus_group. `attachment_ids` lists the
// ready files in list order, the order the group sees them in. Without files
// the field is omitted (null is a 422), so the request is the one sent before
// attachments existed; the files themselves are never part of it.
export function buildTemplateFocusGroupPayload({
  campaign,
  campaignName,
  templateId,
  personaIds,
  summarizerPromptId,
  improvementPlanPromptId,
  attachments = [],
}) {
  const attachmentIds = readyAttachmentIds(attachments);
  return {
    query: campaign ? campaign.description : '',
    template_id: templateId,
    persona_ids: personaIds,
    campaign_name: campaignName,
    campaign_id: campaign.topic_id,
    summarizer_prompt_id: summarizerPromptId ?? null,
    improvement_plan_prompt_id: improvementPlanPromptId ?? null,
    ...(attachmentIds.length ? { attachment_ids: attachmentIds } : {}),
  };
}

// A result's files in run order, numbered as the group saw them ("Material 2
// of 3"). Only `attachments` is read: the backend converts results saved under
// the older single-file key.
export function resultAttachments(result) {
  const attachments = Array.isArray(result?.attachments) ? result.attachments : [];
  return attachments.map((attachment, index) => ({
    attachment,
    position: index + 1,
    total: attachments.length,
  }));
}

// The backend's `{"detail": "..."}` message, or null when there is none to show
// as-is: network errors, 422 validation lists, non-JSON proxy responses.
export function apiErrorDetail(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : null;
}
